import { useLayoutEffect, useState } from "react";

export const APPLY_ANCHOR_ID = "apply";

/** the sticky header floats over the page, so it hides the strip beneath it */
const getHeaderHeight = () => {
  const header = document.querySelector<HTMLElement>(".header");
  return header ? header.getBoundingClientRect().height : 0;
};

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/**
 * React Router v5 has no hash-scroll support, so every "check your approval" CTA
 * scrolls to the form imperatively instead of relying on an href="#apply".
 */
export const scrollToApply = () => {
  const target = document.getElementById(APPLY_ANCHOR_ID);
  if (!target) return;

  const rect = target.getBoundingClientRect();
  const headerHeight = getHeaderHeight();
  const available = window.innerHeight - headerHeight;

  /* Centre the form when it fits under the header; when it is taller than that
   * — the case on phones — pin its top just below instead, so the first field
   * never lands behind the header. scrollIntoView({block:"center"}) cannot make
   * that distinction and put the top of the card off-screen on mobile. */
  const gap = rect.height < available ? (available - rect.height) / 2 : 16;
  const top = Math.max(0, rect.top + window.pageYOffset - headerHeight - gap);

  window.scrollTo({
    top,
    behavior: prefersReducedMotion() ? "auto" : "smooth",
  });

  // move focus to the first field so keyboard users land where the mouse users look
  const firstInput = target.querySelector<HTMLElement>("input, select");
  firstInput?.focus({ preventScroll: true });
};

/**
 * True while the apply form is on screen. The header CTA only earns its space
 * once the form has scrolled away, so it hides while the form is in sight.
 *
 * Returns false when there is no form on the page (/landing, /landing-2), which
 * leaves any CTA visible rather than hiding it forever.
 */
export const useApplyFormInView = () => {
  const [inView, setInView] = useState(false);

  useLayoutEffect(() => {
    const target = document.getElementById(APPLY_ANCHOR_ID);
    if (!target) return undefined;

    if (!("IntersectionObserver" in window)) return undefined;

    let observer: IntersectionObserver | null = null;

    // rebuilt on resize: the header is shorter on phones, and the margin below
    // is a fixed pixel value baked in when the observer is created
    const watch = () => {
      observer?.disconnect();
      const headerHeight = Math.round(getHeaderHeight());

      /* IntersectionObserver reports asynchronously, so seed the state here
       * instead. The form sits in the hero on all three landers, so waiting for
       * the first callback would flash the CTA on load. useLayoutEffect runs
       * before paint, so this re-render is never seen. */
      const rect = target.getBoundingClientRect();
      setInView(rect.bottom > headerHeight && rect.top < window.innerHeight);

      observer = new IntersectionObserver(
        ([entry]) => setInView(entry.isIntersecting),
        { rootMargin: `-${headerHeight}px 0px 0px 0px` },
      );
      observer.observe(target);
    };

    watch();
    window.addEventListener("resize", watch);

    return () => {
      window.removeEventListener("resize", watch);
      observer?.disconnect();
    };
  }, []);

  return inView;
};
